import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { dashboardService } from '../services';
import { DashboardMetric, DashboardPeriodValue } from '../types';
import {
  DASHBOARD_QUERY_STALE_TIME_MS,
  DASHBOARD_QUERY_GC_TIME_MS,
  DASHBOARD_QUERY_REFETCH_ON_WINDOW_FOCUS,
} from './dashboardQueryPolicy';

/**
 * Hook para buscar métricas do dashboard de Atendimento (única área — as
 * visões comercial/suporte mockadas foram removidas em 2026-08-04).
 *
 * @param period - Valor do seletor de período (F81 — shared PeriodPicker):
 *                 `{ mode: 'week' | 'month' | 'year' }` ou
 *                 `{ mode: 'custom', start, end }` (yyyy-mm-dd)
 * @param channelId - Canal de atendimento; ausente = todos os canais
 *
 * @example
 * const { data: metrics, isLoading } = useDashboardMetrics({ mode: 'week' });
 */
export const useDashboardMetrics = (
  period: DashboardPeriodValue = { mode: 'month' },
  channelId?: string
): UseQueryResult<DashboardMetric[], Error> => {
  const isCustom = period.mode === 'custom';
  const customReady = !isCustom || (!!period.start && !!period.end);

  return useQuery({
    queryKey: ['dashboard-metrics', period.mode, period.start, period.end, channelId ?? null],
    queryFn: () =>
      dashboardService.getMetrics(
        period.mode,
        isCustom && period.start && period.end
          ? { start: period.start, end: period.end }
          : undefined,
        channelId
      ),
    enabled: customReady,
    // Política compartilhada com usePerformanceData (D10) — ver
    // dashboardQueryPolicy.ts para o motivo de ser deliberadamente idêntica.
    staleTime: DASHBOARD_QUERY_STALE_TIME_MS,
    gcTime: DASHBOARD_QUERY_GC_TIME_MS,
    refetchOnWindowFocus: DASHBOARD_QUERY_REFETCH_ON_WINDOW_FOCUS,
    retry: 2,
  });
};
